import React, { useState, useEffect, useCallback, useRef,useMemo } from "react";
import { useNavigate,useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../apiWithAccessToken";

import {
  History,
  ArrowLeft,
  MoreVertical,
  Edit,
  Bookmark,
  Search
} from "lucide-react";

const ConversationView = () => {
const [sessions, setSessions] = useState([]); 
const [loadingSessions, setLoadingSessions] = useState(false);
const [activeSessionId] = useState(null);
const navigate = useNavigate();
const isPersistedSession = (session) => {
  return Boolean(session?.created_at || session?.updated_at);
};

const [menuOpenId, setMenuOpenId] = useState(null);  
const [menuAnchor, setMenuAnchor] = useState(null); 
const { onSelectChat } = useOutletContext();
const [showBookmarked, setShowBookmarked] = useState(false);
const { refreshSessions } = useOutletContext();

  // Rename dialog state
  const [renameDialog, setRenameDialog] = useState({
    isOpen: false,
    session: null,
    newTitle: "",
  });

  const openRenameDialog = useCallback((session) => {
  setRenameDialog({
    isOpen: true,
    session,
    newTitle: getSessionTitle(session),
  });
  setMenuOpenId(null);
}, []);

  const [searchQuery, setSearchQuery] = useState("");

  const menuRef = useRef(null);

 useEffect(() => {
  if (!menuOpenId) return;

  const handler = (e) => {
    if (!e.target.closest(".session-menu")) {
      setMenuOpenId(null);
    }
  };

  document.addEventListener("mousedown", handler);
  return () => document.removeEventListener("mousedown", handler);
}, [menuOpenId]);


const fetchSessions = useCallback(async () => {
  try {
    setLoadingSessions(true);

    const response = await api.get("/sessions");


    const list =Array.isArray(response.data) ? response.data : [];
    setSessions(list);

  } catch (err) {

    toast.error("Failed to load conversations");
    console.error(err);
  } finally {
    setLoadingSessions(false);
  }
}, []);

const loadedRef = useRef(false);

  useEffect(() => {
      if (loadedRef.current) return;

    fetchSessions();
      loadedRef.current = true;

  }, [fetchSessions]);
  

  const handleSelectSession = (session) => {
  onSelectChat?.(session);
  navigate("/chatwindow");
};


  const getSessionTitle = (session) => {
    if (session?.title?.trim()) return session.title;
    return "New Chat";
  };


const toggleBookmark = async (sessionId) => {
  try {
    await api.post(`/sessions/${sessionId}/bookmark`);

    // update local list only
    setSessions(prev =>
      prev.map(s =>
        s.id === sessionId
          ? { ...s, is_bookmarked: !s.is_bookmarked }
          : s
      )
    );

    refreshSessions(); 
    toast.success("Bookmark updated");
  } catch (e) {
    toast.error("Failed to update bookmark");
  } finally {
    setMenuOpenId(null);
  }
};

const bookmarkedCount = useMemo(
  () => sessions.filter(s => s.is_bookmarked).length,
  [sessions]
);

const handleRenameSession = async (sessionId, newTitle) => {
    const trimmed = newTitle.trim();

  if (!trimmed) return;
 const originalTitle = getSessionTitle(renameDialog.session);
  if (trimmed === originalTitle) {
    setRenameDialog({ isOpen: false, session: null, newTitle: "" });
    return; //  no API call
  }

  try {
    const { data } = await api.post("/chat-rename", {
      session_id: sessionId,
      title: trimmed,
    });

    setSessions(prev =>
      prev.map(s =>
        s.id === sessionId
          ? { ...s, title: data.title }
          : s
      )
    );

    refreshSessions(); //  sync sidebar
    toast.success("Chat renamed");
  } catch (e) {
    toast.error("Failed to rename chat");
  } finally {
    setRenameDialog({ isOpen: false, session: null, newTitle: "" });
  }
};






  const sortedSessions = useMemo(() => {
  return [...sessions].sort(
    (a, b) => 
      new Date(b.updated_at || b.created_at) -( new Date(a.updated_at || a.created_at))
  
  );
}, [sessions]);


 const displayedSessions = useMemo(() => {
  return sortedSessions
.filter(s => !showBookmarked || s.is_bookmarked)
    .filter(s =>
      !searchQuery ||
      (s.title || "New Chat")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
}, [sortedSessions, showBookmarked, searchQuery]);


  return (
      <div className="page-scroll">

               <div className=" no-scrollbar bg-background text-foreground flex flex-col h-full overflow-y-auto">
               <div className="flex-1 w-full px-4 py-6">
           {/* Header */}
              <div className="sticky top-0 bg-background backdrop-blur-sm border-b border-border px-4 py-4 mb-6 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft size={20} />
              </button>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <History size={20} />
                Conversation History
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {/* Search Bar */}
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 w-64 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <button
                onClick={() => setShowBookmarked(!showBookmarked)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  showBookmarked
                    ? "bg-primary/10 text-primary border border-primary"
                    : "hover:bg-muted border border-transparent"
                }`}
              >
                <Bookmark
                size={18}
                 className={bookmarkedCount > 0 && showBookmarked ? "fill-current" : ""}
                  />

                 {bookmarkedCount > 0 && (
                  <span className="ml-1 bg-primary/20 text-primary text-xs px-2 py-0.5 rounded-full">
                   {bookmarkedCount}
                   </span>
                 )}

              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {loadingSessions ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3"></div>
              <p className="text-base text-muted-foreground">Loading conversations...</p>
            </div>
          </div>
        ) : displayedSessions.length > 0 ? (


          <div className="space-y-4">
            {displayedSessions.map((session) => {
              const isActive = activeSessionId === session.id;
             // const isBookmarked = bookmarkedSessions.includes(session.id);
              const isBookmarked = session.is_bookmarked;

              return (
                <div 
                  key={session.id} 
                  className={`bg-card border border-border rounded-lg overflow-hidden shadow-sm transition-all ${
                    isActive 
                      ? "ring-2 ring-primary/50 shadow-md" 
                      : "hover:shadow-md"
                  }`}
                >
                  <div
                    className="group p-3 hover:bg-muted/30 cursor-pointer transition-colors"
                    onClick={() => handleSelectSession(session)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0 flex items-center gap-3">
                        {isBookmarked && (
                          <Bookmark 
                            size={14} 
                            className="text-primary fill-current flex-shrink-0" 
                          />
                        )}
                        <div className="flex items-center gap-2">
                          <h3 className={`text-sm font-medium truncate ${
                            isActive ? "text-primary" : "text-foreground"
                          }`}>
                            {getSessionTitle(session)}
                          </h3>
                        </div>
                      </div>
                    {/* More Options Button */}
                      <div className="relative menu-container" ref={menuRef}>
                   
                   <button
              onClick={(e) => {
             e.stopPropagation();
               setMenuAnchor(e.currentTarget);
              setMenuOpenId(prev => (prev === session.id ? null : session.id));
              }}
                 className="p-1.5 hover:bg-muted rounded-lg"
            >
            <MoreVertical size={16} />
         </button>

                      </div>                    
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <History size={28} className="text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {searchQuery
                ? "No conversations match your search"
                : showBookmarked
                ? "No bookmarked chats yet"
                : "No conversations yet"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery
                ? "Try adjusting your search terms."
                : showBookmarked
                ? "Bookmark chats to see them here for quick access."
                : "Start your first conversation to see it here."}
            </p>
            {!showBookmarked && !searchQuery && (
              <button
                onClick={() => navigate("/chatwindow")}
                className="px-5 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                Start Chatting
              </button>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="footer-section">
        <div className="legal">
          © 2026 ScatterPie Analytics Pvt. Ltd. •{" "}
          <a href="https://www.scatterpie.io/privacy-policy/">Privacy Policy</a>
        </div>
      </div>

      {/* SESSION MENU OVERLAY */}
      {menuOpenId && menuAnchor && (
        <div className="fixed inset-0 z-[9999]">
          {/* Click outside to close */}
          <div
            className="absolute inset-0"
            onClick={() => setMenuOpenId(null)}
          />

          {/* Popup menu */}
          {(() => {
            const rect = menuAnchor.getBoundingClientRect();
            const menuWidth = 140; // min-w-[140px]
            const menuHeight = 80; // approximate height for 2 items
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // Calculate position
            let top = rect.top + rect.height / 2;
            let left = rect.right + 8;

            // Adjust horizontal position if not enough space on right
            if (left + menuWidth > viewportWidth) {
              left = rect.left - menuWidth - 8;
            }

            // Adjust vertical position if not enough space below
            if (top + menuHeight > viewportHeight) {
              top = rect.top - menuHeight;
            }

            // Ensure menu stays within viewport bounds
            top = Math.max(8, Math.min(top, viewportHeight - menuHeight - 8));
            left = Math.max(8, Math.min(left, viewportWidth - menuWidth - 8));

            return (
             <div
               className="session-menu fixed bg-card border border-border rounded-lg shadow-lg min-w-[140px] py-1"
                style={{ top, left }}
               >

                {(() => {
             const menuSession = displayedSessions.find(s => s.id === menuOpenId);
            const isBookmarked = menuSession?.is_bookmarked;

                
                  return (
           <button
  onClick={() => toggleBookmark(menuOpenId)}
  className="flex items-center gap-2 w-full px-3 py-2 hover:bg-muted"
>
  <Bookmark
    size={14}
    className={isBookmarked ? "fill-current text-primary" : ""}
  />
  <span className="text-xs">
    {isBookmarked ? "Unbookmark" : "Bookmark"}
  </span>
</button>


 );
   })()}


{(() => {
  const session = displayedSessions.find(s => s.id === menuOpenId);
  const canRename = session && isPersistedSession(session);

  return (
    <button
      disabled={!canRename}
      onClick={(e) => {
        e.stopPropagation();

        if (!canRename) {
          toast.info("Send a message to save the chat before renaming");
          return;
        }

        openRenameDialog(session);
        setMenuOpenId(null);
      }}
      className={`flex items-center gap-2 w-full px-3 py-2 hover:bg-muted ${
        !canRename ? "opacity-50 cursor-not-allowed" : ""
      }`}
    >
      <Edit size={14} />
      <span className="text-xs">Rename</span>
    </button>
  );
})()}




              </div>
            );
          })()}
        </div>
      )}

    {renameDialog.isOpen && (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
    onClick={() =>
      setRenameDialog({ isOpen: false, session: null, newTitle: "" })
    }
  >
    <div
      className="w-full max-w-sm rounded-xl bg-background border border-border p-4 shadow-lg"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-foreground">
          Rename conversation
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Give this chat a meaningful name
        </p>
      </div>

      {/* Input */}
      <input
        type="text"
        value={renameDialog.newTitle}
        onChange={(e) =>
          setRenameDialog((prev) => ({
            ...prev,
            newTitle: e.target.value,
          }))
        }
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleRenameSession(
              renameDialog.session.id,
              renameDialog.newTitle
            );
          }
          if (e.key === "Escape") {
            setRenameDialog({ isOpen: false, session: null, newTitle: "" });
          }
        }}
        autoFocus
        placeholder="Conversation title"
        className="
          w-full rounded-lg border border-border bg-background
          px-3 py-2 text-sm text-foreground
          focus:outline-none focus:ring-2 focus:ring-primary
        "
      />

      {/* Actions */}
      <div className="mt-4 flex justify-end gap-2">
        <button
          onClick={() =>
            setRenameDialog({ isOpen: false, session: null, newTitle: "" })
          }
          className="text-sm text-muted-foreground hover:text-foreground transition"
        >
          Cancel
        </button>

        <button
          onClick={() =>
            handleRenameSession(
              renameDialog.session.id,
              renameDialog.newTitle
            )
          }
          disabled={!renameDialog.newTitle.trim()}
          className={`
            rounded-md px-4 py-1.5 text-sm font-medium transition
            ${
              renameDialog.newTitle.trim()
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "bg-muted text-muted-foreground cursor-not-allowed"
            }
          `}
        >
          Save
        </button>
      </div>
    </div>
  </div>
)}


    </div>
    </div>
  );
};

export default ConversationView;